import { UpperCasePipe } from '@angular/common';
import { Component, HostListener, inject, signal } from '@angular/core';
import { PRIMARY_COLOR } from '../../../core/config/theme.config';
import {
  BODY_FONT_OPTIONS,
  FONT_BASE_PRESETS,
  FREE_GOOGLE_FONTS,
  RADIUS_PRESETS,
  RTL_FONT_OPTIONS,
} from '../../../core/helpers/theme-fonts';
import { PageStyle, SidebarStyle, ThemeOverrides } from '../../../core/models/theme-settings.model';
import { LayoutService } from '../../../core/services/common/layout.service';
import { ThemeSettingsService } from '../../../core/services/common/theme-settings.service';

type SettingsSection = 'logos' | 'themeColors' | 'text' | 'buttons' | 'forms' | 'headerFooter' | 'body' | 'fonts' | 'layout';

/**
 * The Settings panel opened by the gear in the top bar, same as the tenant portal's: mode, language, logo and
 * favicon, theme / text / button / form / header / footer / body colours, fonts and layout. It only calls
 * ThemeSettingsService, which applies each change to the page at once and remembers it.
 */
@Component({
  selector: 'app-theme-settings',
  imports: [UpperCasePipe],
  templateUrl: './theme-settings.html',
  styleUrl: './theme-settings.scss',
})
export class ThemeSettings {
  readonly layoutService = inject(LayoutService);
  readonly settings = inject(ThemeSettingsService);

  readonly theme = this.settings.theme;
  readonly colorPresets: readonly { name: string; hex: string }[] = [
    { name: 'Purple', hex: PRIMARY_COLOR },
    { name: 'Emerald', hex: '#10b981' },
    { name: 'Blue', hex: '#2563eb' },
    { name: 'Rose', hex: '#e11d48' },
    { name: 'Amber', hex: '#f59e0b' },
    { name: 'Cyan', hex: '#06b6d4' },
    { name: 'Indigo', hex: '#6366f1' },
    { name: 'Teal', hex: '#14b8a6' },
  ];
  readonly bodyFonts = BODY_FONT_OPTIONS;
  readonly rtlFonts = RTL_FONT_OPTIONS;
  readonly fontBases = FONT_BASE_PRESETS;
  readonly radii = RADIUS_PRESETS;

  readonly menuStyles: readonly { value: SidebarStyle; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'color', label: 'Color' },
  ];
  readonly pageStyles: readonly { value: PageStyle; label: string }[] = [
    { value: 'regular', label: 'Regular' },
    { value: 'classic', label: 'Classic' },
    { value: 'modern', label: 'Modern' },
  ];

  readonly logoPresets = [
    './assets/images/brand-logos/erp-logo-icon.png',
    './assets/images/brand-logos/desktop-logo.png',
    './assets/images/brand-logos/desktop-dark.png',
    './assets/images/brand-logos/toggle-logo.png',
  ] as const;

  readonly faviconPresets = [
    { label: 'App', url: './assets/images/brand-logos/favicon.ico' },
    { label: 'US', url: './assets/images/flags/us_flag.svg' },
    { label: 'UAE', url: './assets/images/flags/uae_flag.svg' },
    { label: 'IN', url: './assets/images/flags/india_flag.svg' },
  ] as const;

  readonly openSection = signal<SettingsSection | null>('themeColors');

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.layoutService.settingsOpen()) {
      this.close();
    }
  }

  close(): void {
    this.layoutService.closeSettings();
  }

  toggleSection(id: SettingsSection): void {
    this.openSection.update((current) => (current === id ? null : id));
  }

  isOpen(id: SettingsSection): boolean {
    return this.openSection() === id;
  }

  setMode(mode: 'light' | 'dark'): void {
    this.layoutService.setTheme(mode);
  }

  setLanguage(code: 'en' | 'ar'): void {
    this.settings.setLanguage(code);
  }

  patch(partial: ThemeOverrides): void {
    this.settings.patch(partial);
  }

  /** A colour input: applied as soon as it changes (an empty value is ignored). */
  onColor(field: keyof ThemeOverrides, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      this.settings.patch({ [field]: value } as ThemeOverrides);
    }
  }

  onPrimary(event: Event): void {
    this.settings.setPrimary((event.target as HTMLInputElement).value);
  }

  onSecondary(event: Event): void {
    this.settings.setSecondary((event.target as HTMLInputElement).value);
  }

  setBodyFont(name: string): void {
    this.settings.patch({ fontBody: name });
  }

  setRtlFont(name: string): void {
    this.settings.patch({ fontRtl: name });
  }

  isPrimaryActive(hex: string): boolean {
    return this.theme().primary === hex.toLowerCase();
  }

  isSecondaryActive(hex: string): boolean {
    return this.theme().secondary.toLowerCase() === hex.toLowerCase();
  }

  fontKnown(name: string): boolean {
    return name in FREE_GOOGLE_FONTS;
  }

  reset(): void {
    this.settings.reset();
  }
}
